/**
 * Per-station coverage as five colour bands — the chart's coverage strip and
 * the table's coverage cell. Replaced the low-coverage hatch / strike-through
 * (2026-10-05, client request): a reader sees HOW covered a station is at a
 * glance, not only whether it crossed `minCoveragePct`.
 *
 * Fixed at 20/40/60/80 %, deliberately independent of the run's
 * `minCoveragePct` — that threshold still drives the ⚠ flag and the
 * "unreliable" footnote, which are what survive a greyscale print.
 *
 * Twin: `server/background/src/functions/processReport/coverageBands.ts`
 * (the PDF). Same boundaries, same colours — change both.
 */

export interface CoverageBand {
  index: 0 | 1 | 2 | 3 | 4;
  name: 'red' | 'orange' | 'yellow' | 'lightGreen' | 'darkGreen';
  /** Lower bound, inclusive, in percent. */
  minPct: number;
  color: string;
  /** Text ink on `color` (the table cell). */
  ink: string;
}

export const COVERAGE_BANDS: readonly CoverageBand[] = [
  { index: 0, name: 'red', minPct: 0, color: '#d64541', ink: '#1a1a19' },
  { index: 1, name: 'orange', minPct: 20, color: '#f08a24', ink: '#1a1a19' },
  { index: 2, name: 'yellow', minPct: 40, color: '#f2c94c', ink: '#1a1a19' },
  { index: 3, name: 'lightGreen', minPct: 60, color: '#9ccc65', ink: '#1a1a19' },
  { index: 4, name: 'darkGreen', minPct: 80, color: '#2e7d32', ink: '#ffffff' },
];

/** The band for a coverage percentage. Missing / non-finite → the lowest. */
export function coverageBand(pct: number | null | undefined): CoverageBand {
  const p = typeof pct === 'number' && Number.isFinite(pct) ? pct : 0;
  for (let i = COVERAGE_BANDS.length - 1; i > 0; i--) if (p >= COVERAGE_BANDS[i].minPct) return COVERAGE_BANDS[i];
  return COVERAGE_BANDS[0];
}

/**
 * A station the reference surface never reaches has nothing to measure: it is
 * EMPTY, not 0 % covered (`station_bins.py` writes `coveragePct: 0` and never
 * flags it low; the corridor's weighted coverage gives it no weight). Painting
 * it red made a 100 %-covered run look half-missed where the design simply
 * ends before the alignment does.
 */
export const NO_REFERENCE_COLOR = '#d4d4cf';

/** Whether a bin has reference cells to be covered — reference cells on a run
 *  with `coverageBasis`, corridor cells before it. Unknown counts as yes. */
export function hasReference(b: { refCellCount?: number | null; binCellCount?: number | null }): boolean {
  const w = b.refCellCount ?? b.binCellCount;
  return typeof w === 'number' ? w > 0 : true;
}
